import React from 'react';
import { createPortal } from 'react-dom';
import '../../../shared/components/layout/StoreDashboardLayout.css';
import './StoreOffers.css';
import ErrorBoundary from '../../../shared/components/ErrorBoundary';
import AiBadge from '../components/AiBadge';
import AiGenerateTargetModal from '../components/AiGenerateTargetModal';
import AiOfferRecommendationPanel from '../components/AiOfferRecommendationPanel';
import CampaignBuilder from '../components/CampaignBuilder';
import OfferLivePreview from '../components/OfferLivePreview';
import DeadHoursPanel from '../components/DeadHoursPanel';
import RetentionBucketsPanel from '../components/RetentionBucketsPanel';
import LoyaltyProgramCard from '../components/LoyaltyProgramCard';
import { ANALYTICS_DEAD_HOURS_SUMMARY } from '../../../shared/constants/endpoints';
import useAiOfferRecommendations from '../hooks/useAiOfferRecommendations';
import useOffers from '../hooks/useOffers';
import useServiceCatalog from '../hooks/useServiceCatalog';
import {
    composeIsoLocal,
    formatLocalDateValue,
    getFutureScheduleDefaults,
    getOfferEffectiveState,
    parseOfferIsoLocal,
    roundUpMinutes
} from '../utils/offerLifecycle';
import { useSelector } from 'react-redux';
import EmptyState from '../../../shared/components/ui/EmptyState';
import { AlertTriangle, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { formatRetryAfter } from '../api/aiOfferRecommendationsApi';
import Skeleton from '../../../shared/components/ui/Skeleton';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';

const OFFERS_ACTIVE_VIEW_STORAGE_KEY = 'offersActiveViewV2';

const normalizeOffersView = (value) => (value === 'campaigns' ? 'campaigns' : 'ai');

const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(value);

const formatCurrency = (value) => `Rs ${formatNumber(value)}`;

const getOfferIconText = (offer) => {
    const discount = String(offer?.discount || '').trim();
    if (!discount || discount === '—') {
        return 'CUT';
    }
    const percentMatch = discount.match(/(\d+(?:\.\d+)?)\s*%/);
    if (percentMatch) {
        return `${percentMatch[1]}%`;
    }
    const amountMatch = discount.match(/(?:rs|₹)\s*(\d+(?:\.\d+)?)/i);
    if (amountMatch) {
        return `₹${amountMatch[1]}`;
    }
    const shortText = discount.replace(/\s+/g, '').slice(0, 4);
    return shortText || 'CUT';
};

const OFFER_ICON_GRADIENTS = [
    'linear-gradient(135deg,#F3774D,#B8431F)',
    'linear-gradient(135deg,#fbbf24,#B45309)',
    'linear-gradient(135deg,#60a5fa,#1e40af)',
    'linear-gradient(135deg,#a78bfa,#4c1d95)',
    'linear-gradient(135deg,#f472b6,#831843)',
    'linear-gradient(135deg,#22d3ee,#0891b2)'
];

const coerceMonthNumber = (value) => {
    if (typeof value === 'number') {
        return value;
    }
    const text = String(value || '').trim();
    if (!text) {
        return Number.NaN;
    }
    if (/^\d{1,2}$/.test(text)) {
        return Number(text);
    }
    const month = text.slice(0, 3).toLowerCase();
    const monthMap = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12
    };
    return monthMap[month] ?? Number.NaN;
};

const pad2 = (value) => String(value).padStart(2, '0');


const normalizeIsoLocalForBackend = (value) => {
    const text = String(value || '').trim();
    if (!text) {
        return null;
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) {
        return `${text}:00`;
    }
    return text;
};

const coerceIsoLocalForInput = (value) => {
    if (!value) {
        return '';
    }

    if (Array.isArray(value) && value.length >= 5) {
        const [year, month, day, hour, minute] = value;
        if ([year, month, day, hour, minute].some((part) => typeof part !== 'number')) {
            return '';
        }
        return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`;
    }

    if (typeof value === 'object') {
        const dateText = typeof value?.date === 'string' ? value.date.trim() : '';
        const timeText = typeof value?.time === 'string' ? value.time.trim() : '';
        if (dateText && timeText) {
            return coerceIsoLocalForInput(`${dateText}T${timeText}`);
        }

        const year = value?.year;
        const month = coerceMonthNumber(value?.monthValue ?? value?.month);
        const day = value?.dayOfMonth ?? value?.day;
        const hour = value?.hour;
        const minute = value?.minute;
        if ([year, month, day, hour, minute].every((part) => typeof part === 'number')) {
            return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`;
        }

        const nestedDate = value?.date;
        const nestedTime = value?.time;
        const nestedYear = nestedDate?.year;
        const nestedMonth = coerceMonthNumber(nestedDate?.monthValue ?? nestedDate?.month);
        const nestedDay = nestedDate?.dayOfMonth ?? nestedDate?.day;
        const nestedHour = nestedTime?.hour;
        const nestedMinute = nestedTime?.minute;
        if ([nestedYear, nestedMonth, nestedDay, nestedHour, nestedMinute].every((part) => typeof part === 'number')) {
            return `${nestedYear}-${pad2(nestedMonth)}-${pad2(nestedDay)}T${pad2(nestedHour)}:${pad2(nestedMinute)}`;
        }
    }

    const text = String(value || '').trim();
    if (!text) {
        return '';
    }
    const normalized = text.includes('T') ? text : text.replace(' ', 'T');
    const [datePart, timePart] = normalized.split('T');
    if (!datePart || !timePart) {
        return '';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return '';
    }
    const match = timePart.match(/^(\d{1,2}):(\d{1,2})/);
    if (!match) {
        return '';
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return '';
    }
    return `${datePart}T${pad2(hour)}:${pad2(minute)}`;
};

const parseIsoLocal = parseOfferIsoLocal;

const parseAppliesOnFromDescription = (description) => {
    const text = String(description || '');
    const firstLine = text.split('\n')[0] || '';
    if (!firstLine.toLowerCase().startsWith('applies on:')) {
        return { appliesOn: 'STORE_WIDE', categories: '', products: '', minCartValue: '' };
    }
    const scope = firstLine.slice('Applies on:'.length).trim();
    if (scope.toLowerCase().startsWith('store-wide')) {
        return { appliesOn: 'STORE_WIDE', categories: '', products: '', minCartValue: '' };
    }
    if (scope.toLowerCase().startsWith('specific categories')) {
        const parts = scope.split(':');
        return { appliesOn: 'CATEGORIES', categories: (parts[1] || '').trim(), products: '', minCartValue: '' };
    }
    if (scope.toLowerCase().startsWith('specific products')) {
        const parts = scope.split(':');
        return { appliesOn: 'PRODUCTS', categories: '', products: (parts[1] || '').trim(), minCartValue: '' };
    }
    if (scope.toLowerCase().startsWith('minimum cart value')) {
        const match = scope.match(/Rs\s+(\d+)/i);
        return { appliesOn: 'MIN_CART', categories: '', products: '', minCartValue: match ? match[1] : '' };
    }
    return { appliesOn: 'STORE_WIDE', categories: '', products: '', minCartValue: '' };
};

const stripAppliesOnPrefix = (description) => {
    const text = String(description || '').trim();
    const lines = text.split('\n');
    if (lines.length === 0) {
        return '';
    }
    if (lines[0].toLowerCase().startsWith('applies on:')) {
        return lines.slice(1).join('\n').trim();
    }
    return text;
};

const formatShortDateLabel = (value) => {
    const date = parseIsoLocal(value);
    if (!date) {
        return '';
    }
    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short'
    }).format(date);
};

const formatCurrencyParts = (value) => ({
    currency: 'Rs',
    amount: formatNumber(value)
});

const CurrencyValue = ({ value }) => {
    const { currency, amount } = formatCurrencyParts(value);

    return (
        <span className="offers-currency-value">
            <span className="offers-currency-value-part">{currency}</span>
            <span className="offers-currency-value-part">{amount}</span>
        </span>
    );
};

const getOfferStateMeta = (offer) => {
    return getOfferEffectiveState(offer);
};

const OffersLoadingSkeleton = () => (
    <section className="offers-workspace" aria-label="Loading offers">
        <div className="offers-control-bar">
            <div className="offers-filter-group" style={{ gap: 8 }}>
                <Skeleton width={110} height={34} />
                <Skeleton width={110} height={34} />
                <Skeleton width={110} height={34} />
            </div>
            <Skeleton width={180} height={34} />
        </div>
        <div className="offers-cards-grid">
            {Array.from({ length: 3 }).map((_, index) => (
                <article className="offers-kpi-card" key={`offers-skeleton-${index}`}>
                    <Skeleton height={14} width="45%" />
                    <Skeleton height={34} width="60%" />
                    <Skeleton height={12} width="80%" />
                </article>
            ))}
        </div>
    </section>
);

// ── Campaign "7 knobs" advanced rules: empty draft, request payload, edit seed ──
const emptyAdvanced = () => ({
    mechanicType: '',
    tiers: [],
    flatPriceValue: '',
    freeItemLabel: '',
    leadTimeMinHours: '',
    leadTimeMaxHours: '',
    membershipTier: '',
    requiresReferral: false,
    timeWindows: [],
    budgetCeiling: '',
    minDiscountFloor: '',
    stackingMode: 'EXCLUSIVE',
    stackPriority: '',
    redemptionMode: 'CODE',
    targetRetentionBucket: '',
});

const buildAdvancedPayload = (a) => {
    const advanced = a || {};
    const num = (v) => (v === '' || v == null ? null : Number(v));
    const tiers = (advanced.tiers || [])
        .filter((t) => t && t.minSpend !== '' && t.value !== '' && t.minSpend != null && t.value != null)
        .map((t) => ({ minSpend: Number(t.minSpend), type: t.type || 'PERCENT', value: Number(t.value) }));
    const windows = (advanced.timeWindows || [])
        .filter((w) => w && w.start && w.end && Array.isArray(w.days) && w.days.length > 0)
        .map((w) => ({ days: w.days, start: w.start, end: w.end }));
    const eligibility = {};
    if (String(advanced.membershipTier || '').trim()) eligibility.membershipTier = String(advanced.membershipTier).trim();
    if (advanced.requiresReferral) eligibility.requiresReferral = true;
    return {
        mechanicType: advanced.mechanicType || null,
        tiersJson: tiers.length ? JSON.stringify(tiers) : null,
        flatPriceValue: num(advanced.flatPriceValue),
        freeItemLabel: String(advanced.freeItemLabel || '').trim() || null,
        leadTimeMinHours: num(advanced.leadTimeMinHours),
        leadTimeMaxHours: num(advanced.leadTimeMaxHours),
        eligibilityJson: Object.keys(eligibility).length ? JSON.stringify(eligibility) : null,
        timeWindowsJson: windows.length ? JSON.stringify(windows) : null,
        budgetCeiling: num(advanced.budgetCeiling),
        minDiscountFloor: num(advanced.minDiscountFloor),
        stackingMode: advanced.stackingMode || 'EXCLUSIVE',
        stackPriority: num(advanced.stackPriority),
        redemptionMode: advanced.redemptionMode || 'CODE',
        targetRetentionBucket: String(advanced.targetRetentionBucket || '').trim() || null,
    };
};

const parseAdvancedFromOffer = (offer) => {
    const arr = (s) => { try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch { return []; } };
    const obj = (s) => { try { const v = JSON.parse(s); return v && typeof v === 'object' ? v : {}; } catch { return {}; } };
    const str = (v) => (v == null ? '' : String(v));
    const eligibility = obj(offer.eligibilityJson);
    return {
        mechanicType: offer.mechanicType || '',
        tiers: arr(offer.tiersJson),
        flatPriceValue: str(offer.flatPriceValue),
        freeItemLabel: offer.freeItemLabel || '',
        leadTimeMinHours: str(offer.leadTimeMinHours),
        leadTimeMaxHours: str(offer.leadTimeMaxHours),
        membershipTier: eligibility.membershipTier || '',
        requiresReferral: !!eligibility.requiresReferral,
        timeWindows: arr(offer.timeWindowsJson),
        budgetCeiling: str(offer.budgetCeiling),
        minDiscountFloor: str(offer.minDiscountFloor),
        stackingMode: offer.stackingMode || 'EXCLUSIVE',
        stackPriority: str(offer.stackPriority),
        redemptionMode: offer.redemptionMode || 'CODE',
        targetRetentionBucket: offer.targetRetentionBucket || '',
    };
};

// Derives the structured discount fields OfferLivePreview expects from the
// human-readable `offer.discount` string, mirroring the parser CampaignBuilder
// runs on the same string. Advanced mechanics (TIERED/FLAT_PRICE/FREE_ITEM) are
// carried by `advanced` and take precedence in the preview regardless.
const parseDiscountForPreview = (discount) => {
    const base = { discountCategory: 'NONE', percentOff: '', flatOff: '', buyQty: '', getQty: '', spendAmount: '', getAmount: '' };
    const text = String(discount || '').trim();
    if (!text) {
        return base;
    }
    let m = text.match(/^(\d{1,2})\s*%\s*off$/i);
    if (m) {
        return { ...base, discountCategory: 'PERCENT', percentOff: m[1] };
    }
    m = text.match(/^Flat\s+Rs\s+(\d+)\s+off$/i);
    if (m) {
        return { ...base, discountCategory: 'FLAT', flatOff: m[1] };
    }
    m = text.match(/^Buy\s+(\d+)\s+Get\s+(\d+)$/i);
    if (m) {
        return { ...base, discountCategory: 'BUY_GET', buyQty: m[1], getQty: m[2] };
    }
    m = text.match(/^Spend\s+Rs\s+(\d+),\s*get\s+Rs\s+(\d+)\s+off$/i);
    if (m) {
        return { ...base, discountCategory: 'BILL_VALUE', spendAmount: m[1], getAmount: m[2] };
    }
    return base;
};

// Maps a stored offer object to the read-only `config` shape OfferLivePreview
// consumes (the same shape CampaignBuilder assembles as previewConfig), so the
// preview modal renders exactly what the customer would see. Mirrors handleEdit's
// offer → draft field mapping.
const mapOfferToPreviewConfig = (offer) => {
    if (!offer) {
        return {};
    }
    const applies = offer.appliesOnType
        ? {
            appliesOn: offer.appliesOnType,
            categories: offer.appliesOnCategories || '',
            products: offer.appliesOnProducts || '',
            minCartValue: offer.appliesOnMinCartValue == null ? '' : String(offer.appliesOnMinCartValue)
        }
        : parseAppliesOnFromDescription(offer.description);
    return {
        title: offer.title || '',
        ...parseDiscountForPreview(offer.discount),
        appliesOn: applies.appliesOn,
        appliesCategories: applies.categories,
        minCartValue: applies.minCartValue,
        audience: offer.audience || 'all',
        validDaysLabel: offer.validDaysLabel || '',
        validityFrom: offer.validityFrom,
        validityUntil: offer.validityUntil,
        maxDiscountCap: offer.maxDiscountCap == null ? '' : String(offer.maxDiscountCap),
        redemptionLimitType: offer.redemptionLimitType || 'none',
        redemptionLimitValue: offer.redemptionLimitValue == null ? '' : String(offer.redemptionLimitValue),
        couponCode: offer.couponCode || '',
        advanced: parseAdvancedFromOffer(offer)
    };
};

const StoreOffers = ({ request, onToast }) => {
    const storeToken = useSelector((state) => state.store.storeToken);
    const [activeView, setActiveViewState] = React.useState(() => {
        if (typeof window === 'undefined') {
            return 'ai';
        }
        return normalizeOffersView(window.localStorage.getItem(OFFERS_ACTIVE_VIEW_STORAGE_KEY));
    });
    const setActiveView = React.useCallback((view) => {
        const normalized = normalizeOffersView(view);
        setActiveViewState(normalized);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(OFFERS_ACTIVE_VIEW_STORAGE_KEY, normalized);
        }
    }, []);
    const [isBuilderOpen, setIsBuilderOpen] = React.useState(false);
    const [editingOfferId, setEditingOfferId] = React.useState(null);
    const [draftTitle, setDraftTitle] = React.useState('');
    const [draftDiscount, setDraftDiscount] = React.useState('');
    const [draftAppliesOn, setDraftAppliesOn] = React.useState('');
    const [draftAppliesCategories, setDraftAppliesCategories] = React.useState('');
    const [draftAppliesProducts, setDraftAppliesProducts] = React.useState('');
    const [draftMinCartValue, setDraftMinCartValue] = React.useState('');
    const [draftRedemptionLimitType, setDraftRedemptionLimitType] = React.useState('none');
    const [draftRedemptionLimitValue, setDraftRedemptionLimitValue] = React.useState('');
    const [draftCouponCode, setDraftCouponCode] = React.useState('');
    const [draftMaxDiscountCap, setDraftMaxDiscountCap] = React.useState('');
    const [draftAudience, setDraftAudience] = React.useState('all');
    const [draftValidDaysLabel, setDraftValidDaysLabel] = React.useState('');
    const [draftTerms, setDraftTerms] = React.useState('');
    const [draftFrom, setDraftFrom] = React.useState('');
    const [draftUntil, setDraftUntil] = React.useState('');
    const [draftDescription, setDraftDescription] = React.useState('');
    const [draftAdvanced, setDraftAdvanced] = React.useState(emptyAdvanced);
    const [scheduleOffer, setScheduleOffer] = React.useState(null);
    const [scheduleFromDate, setScheduleFromDate] = React.useState('');
    const [scheduleFromTime, setScheduleFromTime] = React.useState('');
    const [scheduleUntilDate, setScheduleUntilDate] = React.useState('');
    const [scheduleUntilTime, setScheduleUntilTime] = React.useState('');
    const [deleteOffer, setDeleteOffer] = React.useState(null);
    const [previewOffer, setPreviewOffer] = React.useState(null);
    const [openActionOfferId, setOpenActionOfferId] = React.useState(null);

    const {
        filter,
        setFilter,
        summary,
        selectedType,
        setSelectedType,
        selectedChannels,
        setSelectedChannels,
        toggleChannel,
        channelLabels,
        offerTypeOptions,
        channelOptions,
        offers,
        createOffer,
        updateOffer,
        toggleOfferLifecycle,
        removeOffer,
        refetchOffers,
        canUseRetentionBuckets,
        hasLoadedRetentionCapabilities,
        retentionSummary,
        retentionCustomers,
        selectedRetentionBucket,
        setSelectedRetentionBucket,
        refetchRetentionSummary,
        refetchRetentionCustomers,
        retentionLoading,
        retentionCustomersLoading,
        retentionError,
        loading,
        isInitialLoading,
        error,
        isMutating
    } = useOffers({ request, storeToken, onToast });

    const {
        recommendations: aiOfferRecommendations,
        rules: aiOfferRules,
        rulesLoading: aiOfferRulesLoading,
        fetchRules: fetchAiOfferRules,
        saveRules: saveAiOfferRules,
        refetchRecommendations,
        generateRecommendations,
        publishRecommendation,
        rejectRecommendation,
        loading: aiOfferLoading,
        isInitialLoading: aiOfferInitialLoading,
        error: aiOfferError,
        isMutating: aiOfferMutating,
        isGenerating: aiOfferGenerating,
        generationStatus: aiOfferGenerationStatus,
        generationMessage: aiOfferGenerationMessage,
        lastRateLimitMessage: aiOfferRateLimitMessage,
        generationQuota: aiOfferGenerationQuota
    } = useAiOfferRecommendations({ request, storeToken, onToast });

    const aiGenerationExhausted = Boolean(
        aiOfferGenerationQuota && (aiOfferGenerationQuota.remaining ?? 1) <= 0
    );

    const { services: storeServices, isLoading: isLoadingServices } = useServiceCatalog({
        request,
        storeToken,
        onToast,
        enabled: isBuilderOpen
    });

    React.useEffect(() => {
        if (!draftFrom || !draftUntil) {
            return;
        }
        if (String(draftUntil).localeCompare(String(draftFrom)) <= 0) {
            setDraftUntil('');
        }
    }, [draftFrom, draftUntil]);

    const resetDraft = () => {
        setEditingOfferId(null);
        setDraftTitle('');
        setDraftDiscount('');
        setDraftAppliesOn('');
        setDraftAppliesCategories('');
        setDraftAppliesProducts('');
        setDraftMinCartValue('');
        setDraftRedemptionLimitType('none');
        setDraftRedemptionLimitValue('');
        setDraftCouponCode('');
        setDraftMaxDiscountCap('');
        setDraftAudience('all');
        setDraftValidDaysLabel('');
        setDraftTerms('');
        setDraftFrom('');
        setDraftUntil('');
        setDraftDescription('');
        setDraftAdvanced(emptyAdvanced());
    };

    // T&C is edited as one term per line; persisted as a string array.
    const parseTermsInput = (value) => String(value || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

    const buildDescription = () => {
        const base = String(draftDescription || '').trim();
        return base;
    };

    const handleSaveDraft = async () => {
        try {
            const redemptionLimitValue = (
                draftRedemptionLimitType === 'none'
                    ? null
                    : (draftRedemptionLimitValue ? Number(draftRedemptionLimitValue) : null)
            );
            const payload = {
                title: draftTitle,
                type: selectedType,
                lifecycle: 'paused',
                validityFrom: normalizeIsoLocalForBackend(draftFrom),
                validityUntil: normalizeIsoLocalForBackend(draftUntil),
                discount: draftDiscount || '—',
                description: buildDescription(),
                appliesOnType: draftAppliesOn || 'STORE_WIDE',
                appliesOnCategories: draftAppliesOn === 'CATEGORIES' ? String(draftAppliesCategories || '').trim() : '',
                appliesOnProducts: draftAppliesOn === 'PRODUCTS' ? String(draftAppliesProducts || '').trim() : '',
                appliesOnMinCartValue: draftAppliesOn === 'MIN_CART' && draftMinCartValue ? Number(draftMinCartValue) : null,
                redemptionLimitType: draftRedemptionLimitType,
                redemptionLimitValue,
                couponCode: String(draftCouponCode || '').trim() || null,
                maxDiscountCap: draftMaxDiscountCap ? Number(draftMaxDiscountCap) : null,
                audience: String(draftAudience || 'all').trim() || 'all',
                validDaysLabel: String(draftValidDaysLabel || '').trim() || null,
                terms: parseTermsInput(draftTerms),
                channels: Array.from(selectedChannels),
                ...buildAdvancedPayload(draftAdvanced)
            };
            if (editingOfferId) {
                await updateOffer(editingOfferId, payload);
            } else {
                await createOffer(payload);
            }
            resetDraft();
            setIsBuilderOpen(false);
        } catch (error) {
            return;
        }
    };

    const handlePublish = async () => {
        try {
            const redemptionLimitValue = (
                draftRedemptionLimitType === 'none'
                    ? null
                    : (draftRedemptionLimitValue ? Number(draftRedemptionLimitValue) : null)
            );
            const payload = {
                title: draftTitle,
                type: selectedType,
                lifecycle: 'live',
                validityFrom: normalizeIsoLocalForBackend(draftFrom),
                validityUntil: normalizeIsoLocalForBackend(draftUntil),
                discount: draftDiscount || '—',
                description: buildDescription(),
                appliesOnType: draftAppliesOn || 'STORE_WIDE',
                appliesOnCategories: draftAppliesOn === 'CATEGORIES' ? String(draftAppliesCategories || '').trim() : '',
                appliesOnProducts: draftAppliesOn === 'PRODUCTS' ? String(draftAppliesProducts || '').trim() : '',
                appliesOnMinCartValue: draftAppliesOn === 'MIN_CART' && draftMinCartValue ? Number(draftMinCartValue) : null,
                redemptionLimitType: draftRedemptionLimitType,
                redemptionLimitValue,
                couponCode: String(draftCouponCode || '').trim() || null,
                maxDiscountCap: draftMaxDiscountCap ? Number(draftMaxDiscountCap) : null,
                audience: String(draftAudience || 'all').trim() || 'all',
                validDaysLabel: String(draftValidDaysLabel || '').trim() || null,
                terms: parseTermsInput(draftTerms),
                channels: Array.from(selectedChannels),
                ...buildAdvancedPayload(draftAdvanced)
            };
            if (editingOfferId) {
                await updateOffer(editingOfferId, payload);
            } else {
                await createOffer(payload);
            }
            resetDraft();
            setIsBuilderOpen(false);
        } catch (error) {
            return;
        }
    };

    const handleToggleLifecycle = async (offer) => {
        try {
            await toggleOfferLifecycle(offer);
        } catch (error) {
            return;
        }
    };

    const handleSchedule = async (offer) => {
        if (!offer?.id) {
            return;
        }
        const lifecycle = String(offer?.lifecycle || '').toLowerCase();
        if (lifecycle !== 'paused' && lifecycle !== 'scheduled') {
            onToast?.({ message: 'Only drafts or scheduled campaigns can be scheduled.', variant: 'info' });
            return;
        }
        const defaults = getFutureScheduleDefaults(offer);
        setScheduleOffer(offer);
        setScheduleFromDate(defaults.fromDate);
        setScheduleFromTime(defaults.fromTime);
        setScheduleUntilDate(defaults.untilDate);
        setScheduleUntilTime(defaults.untilTime);
    };

    const handleConfirmSchedule = async () => {
        if (!scheduleOffer?.id) {
            return;
        }
        if (!scheduleFromDate || !scheduleFromTime || !scheduleUntilDate || !scheduleUntilTime) {
            onToast?.({ message: 'Select both start and end date/time to schedule.', variant: 'error' });
            return;
        }
        const scheduleFrom = normalizeIsoLocalForBackend(composeIsoLocal(scheduleFromDate, scheduleFromTime));
        const scheduleUntil = normalizeIsoLocalForBackend(composeIsoLocal(scheduleUntilDate, scheduleUntilTime));
        if (!scheduleFrom || !scheduleUntil) {
            onToast?.({ message: 'Invalid schedule date/time.', variant: 'error' });
            return;
        }
        const scheduleFromDateTime = parseIsoLocal(scheduleFrom);
        const scheduleUntilDateTime = parseIsoLocal(scheduleUntil);
        const minimumStart = roundUpMinutes(new Date(), 15);
        if (!scheduleFromDateTime || !scheduleUntilDateTime) {
            onToast?.({ message: 'Invalid schedule date/time.', variant: 'error' });
            return;
        }
        if (scheduleFromDateTime.getTime() < minimumStart.getTime()) {
            onToast?.({ message: 'Start time cannot be in the past.', variant: 'error' });
            return;
        }
        if (String(scheduleUntil).localeCompare(String(scheduleFrom)) <= 0) {
            onToast?.({ message: 'End time must be after start time.', variant: 'error' });
            return;
        }

        try {
            await updateOffer(scheduleOffer.id, {
                lifecycle: 'scheduled',
                statusLabel: 'Scheduled for later',
                validityFrom: scheduleFrom,
                validityUntil: scheduleUntil
            });
        } catch (error) {
            return;
        }
        setScheduleOffer(null);
        setScheduleFromDate('');
        setScheduleFromTime('');
        setScheduleUntilDate('');
        setScheduleUntilTime('');
    };

    const handleEdit = (offer) => {
        if (!offer?.id) {
            return;
        }
        setEditingOfferId(offer.id);
        setDraftTitle(offer.title || '');
        setDraftDiscount(offer.discount || '');
        const applies = offer.appliesOnType
            ? {
                appliesOn: offer.appliesOnType,
                categories: offer.appliesOnCategories || '',
                products: offer.appliesOnProducts || '',
                minCartValue: offer.appliesOnMinCartValue == null ? '' : String(offer.appliesOnMinCartValue)
            }
            : parseAppliesOnFromDescription(offer.description);
        setDraftAppliesOn(applies.appliesOn);
        setDraftAppliesCategories(applies.categories);
        setDraftAppliesProducts(applies.products);
        setDraftMinCartValue(applies.minCartValue);
        setDraftRedemptionLimitType(offer.redemptionLimitType || 'none');
        setDraftRedemptionLimitValue(offer.redemptionLimitValue == null ? '' : String(offer.redemptionLimitValue));
        setDraftCouponCode(offer.couponCode || '');
        setDraftMaxDiscountCap(offer.maxDiscountCap == null ? '' : String(offer.maxDiscountCap));
        setDraftAudience(offer.audience || 'all');
        setDraftValidDaysLabel(offer.validDaysLabel || '');
        setDraftTerms(Array.isArray(offer.terms) ? offer.terms.join('\n') : '');
        const existingFrom = coerceIsoLocalForInput(offer.validityFrom);
        const existingUntil = coerceIsoLocalForInput(offer.validityUntil);
        const effectiveState = getOfferStateMeta(offer);
        if (effectiveState.key === 'active') {
            setDraftFrom(existingFrom);
            setDraftUntil(existingUntil);
        } else {
            const defaults = getFutureScheduleDefaults(offer);
            const minimumStart = roundUpMinutes(new Date(), 15);
            const currentFromDate = existingFrom ? parseIsoLocal(existingFrom) : null;
            const currentUntilDate = existingUntil ? parseIsoLocal(existingUntil) : null;
            const fallbackFrom = composeIsoLocal(defaults.fromDate, defaults.fromTime);
            const fallbackUntil = composeIsoLocal(defaults.untilDate, defaults.untilTime);
            setDraftFrom(currentFromDate && currentFromDate.getTime() >= minimumStart.getTime() ? existingFrom : fallbackFrom);
            setDraftUntil(currentUntilDate && currentFromDate && currentUntilDate.getTime() > currentFromDate.getTime() ? existingUntil : fallbackUntil);
        }
        setDraftDescription(offer.description || stripAppliesOnPrefix(offer.description || ''));
        setDraftAdvanced(parseAdvancedFromOffer(offer));
        setSelectedType(offer.type || 'Standard');
        setSelectedChannels(new Set(Array.isArray(offer.channels) ? offer.channels : []));
        setIsBuilderOpen(true);
    };

    const handleRemove = (offer) => {
        if (!offer?.id) {
            return;
        }
        setDeleteOffer(offer);
    };

    const handleConfirmRemove = async () => {
        if (!deleteOffer?.id) {
            return;
        }
        try {
            await removeOffer(deleteOffer.id);
        } catch (error) {
            return;
        }
        setDeleteOffer(null);
    };

    const handlePublishAiOffer = async (recommendationId, payload) => {
        await publishRecommendation(recommendationId, payload);
        await refetchOffers().catch(() => null);
    };

    const handleDismissAiOffer = async (recommendationId, reason) => {
        await rejectRecommendation(recommendationId, reason);
    };

    const handleRefreshRetention = async () => {
        try {
            await refetchRetentionSummary();
            await refetchRetentionCustomers(selectedRetentionBucket);
        } catch (error) {
            return;
        }
    };

    // Single AI-generation entry point: the merchant always picks the audience
    // first ("Who is this offer for?"). null = whole-store smart mix, otherwise
    // one retention bucket chosen in the target picker modal.
    const [aiTargetPickerOpen, setAiTargetPickerOpen] = React.useState(false);
    // Rules modal open flag lives here so the button can sit in the page header.
    const [aiRulesOpen, setAiRulesOpen] = React.useState(false);

    // Quota chip next to "Generate AI Offer": remaining OFFERS in the window, or
    // the reset countdown. Budget is offers (not runs), so a 1-offer dead-hours
    // fill only spends one and the rest stay available.
    const aiQuotaResetText = aiGenerationExhausted && aiOfferGenerationQuota?.retryAtMs
        ? formatRetryAfter(Math.round((aiOfferGenerationQuota.retryAtMs - Date.now()) / 1000))
        : null;
    const aiQuotaChipText = aiOfferGenerationQuota == null
        ? null
        : aiGenerationExhausted
            ? (aiQuotaResetText ? `Limit reached · resets in ${aiQuotaResetText}` : 'Offer limit reached')
            : `${aiOfferGenerationQuota.remaining} of ${aiOfferGenerationQuota.limit} offers left`;

    const handleGenerateForTarget = async (plan) => {
        setAiTargetPickerOpen(false);
        if (aiGenerationExhausted) {
            return;
        }
        if (!plan || plan.type === 'all' || !Array.isArray(plan.allocations) || plan.allocations.length === 0) {
            // Whole-store smart mix: the AI plans the full offer set itself.
            try {
                await generateRecommendations({ forceRefresh: true });
            } catch (error) {
                // Toast + rate-limit banner are already surfaced by the hook's onError.
            }
            return;
        }
        // Target plan: one run generates the merchant's allocation (sums to 5).
        // Entries may mix retention buckets with an ALL slice — general offers
        // for the whole customer base sharing the same offer budget.
        try {
            await generateRecommendations({
                forceRefresh: true,
                retentionBucketPlan: plan.allocations.map((allocation) => ({
                    bucket: allocation.bucket,
                    displayName: allocation.displayName,
                    suggestedAction: allocation.suggestedAction,
                    customerCount: allocation.customerCount,
                    offerCount: allocation.offerCount
                })),
                retentionExpectedReturnCycleDays: retentionSummary?.expectedReturnCycleDays || retentionCustomers?.expectedReturnCycleDays || null,
                merchantNote: 'Generate the planned number of campaigns for each target group. Avoid unnecessary discounts.'
            });
        } catch (error) {
            // Toast + rate-limit banner are already surfaced by the hook's onError.
            return;
        }
    };

    // CP-100 — dead-hour yield engine: fetch the store's emptiest recurring
    // windows once per AI-view visit; the panel is additive and never blocks.
    const [deadHoursSummary, setDeadHoursSummary] = React.useState(null);

    React.useEffect(() => {
        if (activeView !== 'ai' || typeof request !== 'function' || !storeToken) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await request(ANALYTICS_DEAD_HOURS_SUMMARY, { method: 'GET', requestName: 'dead-hours-summary' });
                if (!cancelled) setDeadHoursSummary(response?.data || null);
            } catch (error) {
                if (!cancelled) setDeadHoursSummary(null);
            }
        })();
        return () => { cancelled = true; };
    }, [activeView, request, storeToken]);

    const handleFillDeadHours = async (windows = []) => {
        if (aiGenerationExhausted || !Array.isArray(windows) || windows.length === 0) {
            return;
        }
        const windowText = windows.map((window) => window.label).join(', ');
        try {
            await generateRecommendations({
                forceRefresh: true,
                retentionBucket: deadHoursSummary?.suggestedBucket || 'DUE_SOON',
                merchantGoal: 'Fill recurring dead hours with an off-peak offer',
                merchantNote: `The store runs far below normal traffic in these windows: ${windowText}. `
                    + 'Generate one off-peak campaign valid in those windows targeting customers due for '
                    + 'their next visit. Prefer added value over deep discounts.'
            });
        } catch (error) {
            // Toast + rate-limit banner are already surfaced by the hook's onError.
            return;
        }
    };

    // CP-111 (S3): the Spotlight/Discover placement choice was removed — the
    // platform computes placement per customer (scored feed); no cap, no toggle.

    React.useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }

        const hasOpenModal = isBuilderOpen || Boolean(scheduleOffer) || Boolean(deleteOffer) || Boolean(previewOffer);
        const body = document.body;
        if (hasOpenModal) {
            body.classList.add('modal-open');
        } else {
            body.classList.remove('modal-open');
        }

        if (!hasOpenModal) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                if (deleteOffer) {
                    setDeleteOffer(null);
                    return;
                }
                if (previewOffer) {
                    setPreviewOffer(null);
                    return;
                }
                if (scheduleOffer) {
                    setScheduleOffer(null);
                    return;
                }
                setIsBuilderOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
            body.classList.remove('modal-open');
        };
    }, [deleteOffer, isBuilderOpen, scheduleOffer, previewOffer]);

    React.useEffect(() => {
        if (typeof document === 'undefined' || openActionOfferId == null) {
            return undefined;
        }

        const closeActions = () => setOpenActionOfferId(null);
        document.addEventListener('click', closeActions);
        return () => document.removeEventListener('click', closeActions);
    }, [openActionOfferId]);

    const builderModal = isBuilderOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
                className="zone-modal-overlay offers-builder-overlay"
                role="presentation"
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                        setIsBuilderOpen(false);
                    }
                }}
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        setIsBuilderOpen(false);
                    }
                }}
            >
                <div
                    className="zone-modal offers-builder-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Create campaign"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="zone-modal-header offers-builder-modal-header">
                        <div>
                            <h3>{editingOfferId ? 'Edit campaign' : 'Create campaign'}</h3>
                            <p>{editingOfferId ? 'Update campaign settings, placement channels, and offer details before publishing.' : 'Configure campaign type, placement channels, validity, and discount.'}</p>
                        </div>
                        <button
                            className="btn ghost"
                            type="button"
                            onClick={() => setIsBuilderOpen(false)}
                        >
                            Close
                        </button>
                    </div>
                    <CampaignBuilder
                        selectedChannels={selectedChannels}
                        selectedType={selectedType}
                        setSelectedType={setSelectedType}
                        channelOptions={channelOptions}
                        channelLabels={channelLabels}
                        offerTypeOptions={offerTypeOptions}
                        toggleChannel={toggleChannel}
                        title={draftTitle}
                        onTitleChange={setDraftTitle}
                        discount={draftDiscount}
                        onDiscountChange={setDraftDiscount}
                        appliesOn={draftAppliesOn}
                        onAppliesOnChange={setDraftAppliesOn}
                        appliesCategories={draftAppliesCategories}
                        onAppliesCategoriesChange={setDraftAppliesCategories}
                        storeServices={storeServices}
                        isLoadingServices={isLoadingServices}
                        minCartValue={draftMinCartValue}
                        onMinCartValueChange={setDraftMinCartValue}
                        redemptionLimitType={draftRedemptionLimitType}
                        onRedemptionLimitTypeChange={setDraftRedemptionLimitType}
                        redemptionLimitValue={draftRedemptionLimitValue}
                        onRedemptionLimitValueChange={setDraftRedemptionLimitValue}
                        couponCode={draftCouponCode}
                        onCouponCodeChange={setDraftCouponCode}
                        maxDiscountCap={draftMaxDiscountCap}
                        onMaxDiscountCapChange={setDraftMaxDiscountCap}
                        audience={draftAudience}
                        onAudienceChange={setDraftAudience}
                        validDaysLabel={draftValidDaysLabel}
                        onValidDaysLabelChange={setDraftValidDaysLabel}
                        terms={draftTerms}
                        onTermsChange={setDraftTerms}
                        validityFrom={draftFrom}
                        onValidityFromChange={setDraftFrom}
                        validityUntil={draftUntil}
                        onValidityUntilChange={setDraftUntil}
                        advanced={draftAdvanced}
                        onAdvancedChange={(p) => setDraftAdvanced((prev) => ({ ...prev, ...p }))}
                        onSaveDraft={handleSaveDraft}
                        onPublish={handlePublish}
                        isSubmitting={isMutating}
                    />
                </div>
            </div>,
            document.body
        )
        : null;

    const scheduleModal = scheduleOffer && typeof document !== 'undefined'
        ? createPortal(
            <div
                className="zone-modal-overlay offers-builder-overlay"
                role="presentation"
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                        setScheduleOffer(null);
                    }
                }}
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        setScheduleOffer(null);
                    }
                }}
            >
                <div
                    className="zone-modal offers-builder-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Schedule campaign"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="zone-modal-header offers-builder-modal-header">
                        <div>
                            <h3>Schedule campaign</h3>
                            <p>Pick when this draft should go live.</p>
                        </div>
                        <button className="btn ghost" type="button" onClick={() => setScheduleOffer(null)}>
                            Close
                        </button>
                    </div>

                    <div className="offers-form-grid" style={{ padding: '0 22px 22px' }}>
                        {(() => {
                            const now = new Date();
                            const todayValue = formatLocalDateValue(now);
                            const maxDate = new Date(now.getTime());
                            maxDate.setFullYear(maxDate.getFullYear() + 1);
                            const maxDateValue = formatLocalDateValue(maxDate);
                            const minimumStart = roundUpMinutes(now, 15);
                            const minimumStartDate = formatLocalDateValue(minimumStart);
                            const minimumStartTime = `${pad2(minimumStart.getHours())}:${pad2(minimumStart.getMinutes())}`;

                            const scheduleFrom = composeIsoLocal(scheduleFromDate, scheduleFromTime);
                            const minUntilDate = scheduleFromDate || minimumStartDate;
                            const minUntilTime = scheduleUntilDate && scheduleFromDate && scheduleUntilDate === scheduleFromDate
                                ? (scheduleFromTime || minimumStartTime)
                                : (scheduleUntilDate === minimumStartDate ? minimumStartTime : '00:00');
                            const scheduleUntil = composeIsoLocal(scheduleUntilDate, scheduleUntilTime);
                            const scheduleFromValue = scheduleFrom ? parseIsoLocal(scheduleFrom) : null;
                            const scheduleUntilValue = scheduleUntil ? parseIsoLocal(scheduleUntil) : null;
                            const scheduleOk = Boolean(
                                scheduleFromValue
                                && scheduleUntilValue
                                && scheduleFromValue.getTime() >= minimumStart.getTime()
                                && scheduleUntilValue.getTime() > scheduleFromValue.getTime()
                            );

                            return (
                                <>
                                    <div className="offers-field">
                                        <label>Start</label>
                                        <div className="offers-datetime-row">
                                            <input
                                                className="analytics-select"
                                                type="date"
                                                min={minimumStartDate}
                                                max={maxDateValue}
                                                value={scheduleFromDate}
                                                onChange={(event) => {
                                                    const nextDate = event.target.value;
                                                    if (!nextDate) {
                                                        setScheduleFromDate('');
                                                        setScheduleFromTime('');
                                                        return;
                                                    }
                                                    const nextTime = nextDate === minimumStartDate
                                                        ? ((scheduleFromTime && scheduleFromTime >= minimumStartTime) ? scheduleFromTime : minimumStartTime)
                                                        : (scheduleFromTime || minimumStartTime);
                                                    setScheduleFromDate(nextDate);
                                                    setScheduleFromTime(nextTime);
                                                }}
                                            />
                                            <div className="offers-time-input-wrap">
                                                <input
                                                    className="time-input offers-time-input"
                                                    type="time"
                                                    step={900}
                                                    min={scheduleFromDate === minimumStartDate ? minimumStartTime : '00:00'}
                                                    value={scheduleFromTime}
                                                    disabled={!scheduleFromDate}
                                                    onChange={(event) => {
                                                        const nextTime = event.target.value;
                                                        if (scheduleFromDate === minimumStartDate && nextTime < minimumStartTime) {
                                                            setScheduleFromTime(minimumStartTime);
                                                            return;
                                                        }
                                                        setScheduleFromTime(nextTime);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="offers-field">
                                        <label>End</label>
                                        <div className="offers-datetime-row">
                                            <input
                                                className="analytics-select"
                                                type="date"
                                                min={minUntilDate}
                                                max={maxDateValue}
                                                value={scheduleUntilDate}
                                                onChange={(event) => setScheduleUntilDate(event.target.value)}
                                            />
                                            <div className="offers-time-input-wrap">
                                                <input
                                                    className="time-input offers-time-input"
                                                    type="time"
                                                    step={900}
                                                    min={minUntilTime}
                                                    value={scheduleUntilTime}
                                                    disabled={!scheduleUntilDate}
                                                    onChange={(event) => setScheduleUntilTime(event.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="offers-composer-actions" style={{ gridColumn: '1 / -1' }}>
                                        <button className="btn ghost" type="button" onClick={() => setScheduleOffer(null)}>
                                            Cancel
                                        </button>
                                        <button className="btn primary" type="button" onClick={handleConfirmSchedule} disabled={isMutating || !scheduleOk}>
                                            Schedule
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>,
            document.body
        )
        : null;

    const deleteModal = deleteOffer ? (
        <ConfirmDialog
            open={Boolean(deleteOffer)}
            title="Delete campaign?"
            description={(
                <p>
                    This will permanently delete <strong>{deleteOffer?.title || 'this campaign'}</strong>. This action cannot be undone.
                </p>
            )}
            confirmLabel={isMutating ? 'Deleting...' : 'Delete Campaign'}
            confirmVariant="danger"
            busy={isMutating}
            onCancel={() => setDeleteOffer(null)}
            onConfirm={handleConfirmRemove}
        />
    ) : null;

    const previewModal = previewOffer && typeof document !== 'undefined'
        ? createPortal(
            <div
                className="zone-modal-overlay offers-preview-overlay"
                role="presentation"
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                        setPreviewOffer(null);
                    }
                }}
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        setPreviewOffer(null);
                    }
                }}
            >
                <div
                    className="zone-modal offers-preview-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Preview ${previewOffer.title || 'offer'}`}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="zone-modal-header offers-preview-modal-header">
                        <div className="offers-preview-heading">
                            <span className="offers-preview-eyebrow">Offer preview</span>
                            <div className="offers-preview-title-row">
                                <h3>{previewOffer.title || 'Untitled offer'}</h3>
                                {(() => {
                                    const state = getOfferStateMeta(previewOffer);
                                    return (
                                        <span className={`offers-table-status-pill ${state.key}`}>
                                            {state.statusText}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                        <button
                            className="offers-preview-close"
                            type="button"
                            aria-label="Close preview"
                            onClick={() => setPreviewOffer(null)}
                        >
                            <X size={18} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                    </div>
                    <div className="offers-preview-modal-body">
                        <dl className="offers-preview-details">
                            <div className="offers-preview-detail">
                                <dt>Valid from</dt>
                                <dd>{previewOffer.validityFrom ? formatShortDateLabel(previewOffer.validityFrom) : '—'}</dd>
                            </div>
                            <div className="offers-preview-detail">
                                <dt>Valid until</dt>
                                <dd>{previewOffer.validityUntil ? formatShortDateLabel(previewOffer.validityUntil) : '—'}</dd>
                            </div>
                            <div className="offers-preview-detail">
                                <dt>Redemptions</dt>
                                <dd>{formatNumber(previewOffer.redeemed)} / {previewOffer.redemptionLimitType === 'global' && previewOffer.redemptionLimitValue
                                    ? formatNumber(previewOffer.redemptionLimitValue)
                                    : '—'}</dd>
                                {previewOffer.redemptionLimitType === 'per_user' && previewOffer.redemptionLimitValue ? (
                                    <span className="offers-preview-detail-hint">Up to {formatNumber(previewOffer.redemptionLimitValue)} per customer</span>
                                ) : null}
                            </div>
                            <div className="offers-preview-detail">
                                <dt>Created</dt>
                                <dd>{previewOffer.createdAt ? formatShortDateLabel(previewOffer.createdAt) : '—'}</dd>
                            </div>
                        </dl>
                        <OfferLivePreview config={mapOfferToPreviewConfig(previewOffer)} layout="split" />
                    </div>
                    <div className="zone-modal-footer offers-preview-modal-footer">
                        <button
                            className="btn ghost danger"
                            type="button"
                            onClick={() => { const offer = previewOffer; setPreviewOffer(null); handleRemove(offer); }}
                        >
                            Delete offer
                        </button>
                        <button
                            className="btn primary"
                            type="button"
                            onClick={() => { const offer = previewOffer; setPreviewOffer(null); handleEdit(offer); }}
                        >
                            Edit offer
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )
        : null;

    const showInitialLoading = isInitialLoading && offers.length === 0;
    const showListError = Boolean(error) && !loading && offers.length === 0;
    const offerBuckets = React.useMemo(() => {
        const live = offers.filter((offer) => getOfferStateMeta(offer).key === 'active');
        const drafts = offers.filter((offer) => ['paused', 'scheduled'].includes(getOfferStateMeta(offer).key));
        const ended = offers.filter((offer) => getOfferStateMeta(offer).key === 'expired');
        return { live, drafts, ended };
    }, [offers]);
    const visibleOffers = React.useMemo(() => {
        if (filter === 'live') return offerBuckets.live;
        if (filter === 'drafts') return offerBuckets.drafts;
        if (filter === 'ended') return offerBuckets.ended;
        return offers;
    }, [filter, offerBuckets, offers]);
    const filterTabs = [
        { key: 'all', label: `All (${offers.length})` },
        { key: 'live', label: `Live (${offerBuckets.live.length})` },
        { key: 'drafts', label: `Drafts (${offerBuckets.drafts.length})` },
        { key: 'ended', label: 'Ended' }
    ];
    const topOffer = React.useMemo(() => (
        offers.reduce((best, offer) => (
            Number(offer?.redemptionsCount || 0) > Number(best?.redemptionsCount || 0) ? offer : best
        ), null)
    ), [offers]);
    const topOfferLabel = topOffer?.title ? String(topOffer.title).trim() : '—';

    return (
        <ErrorBoundary level="feature">
            <div className="store-offers-shell">
                {builderModal}
                {scheduleModal}
                {deleteModal}
                {previewModal}
                <section className="offers-page-header">
                    <div className="offers-page-header-copy">
                        <div className="offers-page-title-row">
                            <h3 className="offers-page-title">Offers &amp; promotions</h3>
                            <AiBadge title="AI-powered offers" />
                        </div>
                        <p className="offers-page-subtitle">
                            {offerBuckets.live.length} live · {offerBuckets.drafts.length} draft · {formatCurrency(summary.influencedRevenue)} attributed revenue
                        </p>
                    </div>
                    <div className="offers-page-header-actions">
                        {activeView === 'ai' ? (
                            <>
                                {aiQuotaChipText ? (
                                    <span className={`ai-offer-quota-chip ${aiGenerationExhausted ? 'exhausted' : ''}`}>
                                        {aiQuotaChipText}
                                    </span>
                                ) : null}
                                <button
                                    className="btn ghost"
                                    type="button"
                                    onClick={() => setAiRulesOpen(true)}
                                    disabled={aiOfferRulesLoading || aiOfferGenerating}
                                    aria-label="Configure AI offer rules"
                                >
                                    <SlidersHorizontal size={15} />
                                    Rules
                                </button>
                                <button
                                    className="btn primary offers-new-offer-btn"
                                    type="button"
                                    onClick={() => setAiTargetPickerOpen(true)}
                                    disabled={aiGenerationExhausted || aiOfferGenerating}
                                >
                                    <Sparkles size={15} />
                                    Generate AI Offer
                                </button>
                            </>
                        ) : (
                            <button
                                className="btn primary offers-new-offer-btn"
                                type="button"
                                onClick={() => setIsBuilderOpen(true)}
                            >
                                Create offer
                            </button>
                        )}
                    </div>
                </section>

                <div className="offers-seg offers-subnav" role="tablist" aria-label="Offers views">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeView === 'ai'}
                        className={`offers-seg-item offers-subnav-item ${activeView === 'ai' ? 'on' : ''}`}
                        onClick={() => setActiveView('ai')}
                    >
                        AI Studio
                        {Array.isArray(aiOfferRecommendations) && aiOfferRecommendations.length > 0 ? (
                            <span className="offers-subnav-count">{aiOfferRecommendations.length}</span>
                        ) : null}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeView === 'campaigns'}
                        className={`offers-seg-item offers-subnav-item ${activeView === 'campaigns' ? 'on' : ''}`}
                        onClick={() => setActiveView('campaigns')}
                    >
                        Campaigns
                        <span className="offers-subnav-count">{offers.length}</span>
                    </button>
                </div>

                {activeView === 'ai' ? (
                    <>
                        <DeadHoursPanel
                            summary={deadHoursSummary}
                            onFillWithOffer={handleFillDeadHours}
                            generateDisabled={aiGenerationExhausted}
                            generating={aiOfferGenerating}
                        />

                        <AiGenerateTargetModal
                            open={aiTargetPickerOpen}
                            onClose={() => setAiTargetPickerOpen(false)}
                            onGenerate={handleGenerateForTarget}
                            buckets={retentionSummary?.buckets}
                            generationQuota={aiOfferGenerationQuota}
                            busy={aiOfferGenerating}
                        />

                        <LoyaltyProgramCard request={request} onToast={onToast} />

                        <RetentionBucketsPanel
                            canUseRetentionBuckets={canUseRetentionBuckets}
                            hasLoadedCapabilities={hasLoadedRetentionCapabilities}
                            summary={retentionSummary}
                            customers={retentionCustomers}
                            selectedBucket={selectedRetentionBucket}
                            onSelectBucket={setSelectedRetentionBucket}
                            onRefresh={handleRefreshRetention}
                            loading={retentionLoading}
                            customersLoading={retentionCustomersLoading}
                            error={retentionError}
                        />

                        <AiOfferRecommendationPanel
                            recommendations={aiOfferRecommendations}
                            loading={aiOfferLoading}
                            isInitialLoading={aiOfferInitialLoading}
                            isMutating={aiOfferMutating}
                            isGenerating={aiOfferGenerating}
                            generationStatus={aiOfferGenerationStatus}
                            generationMessage={aiOfferGenerationMessage}
                            error={aiOfferError}
                            lastRateLimitMessage={aiOfferRateLimitMessage}
                            generationQuota={aiOfferGenerationQuota}
                            onRefresh={refetchRecommendations}
                            onPublish={handlePublishAiOffer}
                            onDismiss={handleDismissAiOffer}
                            rules={aiOfferRules}
                            rulesLoading={aiOfferRulesLoading}
                            onFetchRules={fetchAiOfferRules}
                            rulesOpen={aiRulesOpen}
                            onCloseRules={() => setAiRulesOpen(false)}
                            onSaveRules={saveAiOfferRules}
                        />
                    </>
                ) : (
                <>
                <section className="offers-kpi-grid" aria-label="Offer performance summary">
                    <article className="offers-kpi-card">
                        <p className="offers-kpi-label">Attributed revenue</p>
                        <p className="offers-kpi-value offers-kpi-value-accent">
                            <CurrencyValue value={summary.influencedRevenue} />
                        </p>
                        <p className="offers-kpi-meta">From completed offer visits</p>
                    </article>
                    <article className="offers-kpi-card">
                        <p className="offers-kpi-label">Redemptions</p>
                        <p className="offers-kpi-value">{formatNumber(summary.totalRedemptions)}</p>
                        <p className="offers-kpi-meta">{formatNumber(summary.totalClaims)} claims · completed visits only</p>
                    </article>
                    <article className="offers-kpi-card">
                        <p className="offers-kpi-label">Redemption rate</p>
                        <p className="offers-kpi-value">{summary.redemptionRate}%</p>
                        <p className="offers-kpi-meta">{formatNumber(summary.totalImpressions)} views</p>
                    </article>
                    <article className="offers-kpi-card">
                        <p className="offers-kpi-label">Top offer</p>
                        <p className="offers-kpi-value offers-kpi-value-text">{topOfferLabel}</p>
                        <p className="offers-kpi-meta">{formatNumber(topOffer?.redemptionsCount || 0)} redemptions</p>
                    </article>
                </section>


                {showInitialLoading ? (
                    <OffersLoadingSkeleton />
                ) : showListError ? (
                    <EmptyState
                        icon={<AlertTriangle size={20} />}
                        title="Couldn't load campaigns"
                        description="There was a problem fetching campaign data. Try again."
                        action={{ label: 'Retry', onClick: () => refetchOffers().catch(() => null) }}
                    />
                ) : (
                    <section className="offers-kit-card">
                        <div className="offers-kit-card-head">
                            <h3>All offers</h3>
                            <div className="offers-kit-card-head-actions">
                                <div className="offers-seg" role="tablist" aria-label="Offer filters">
                                    {filterTabs.map((item) => (
                                        <button
                                            key={item.key}
                                            type="button"
                                            className={`offers-seg-item ${filter === item.key ? 'on' : ''}`}
                                            onClick={() => setFilter(item.key)}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {visibleOffers.length === 0 && (
                            <div className="offers-empty-state">
                                <h4>{offers.length === 0 ? 'No offers found' : 'No offers match this filter'}</h4>
                                <p>
                                    {offers.length === 0
                                        ? 'Create a new offer to start showing promotions for this store.'
                                        : 'Try another filter or create a new offer.'}
                                </p>
                            </div>
                        )}

                        {visibleOffers.length > 0 ? (
                            <div className="data-table-wrap offers-table-wrap">
                                <table className="data-table data-table--cards offers-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Offer</th>
                                            <th scope="col" data-align="center">Status</th>
                                            <th scope="col" data-align="center">Redemptions</th>
                                            <th scope="col" data-align="center">Revenue</th>
                                            <th scope="col" data-align="center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleOffers.map((offer, index) => {
                                            const state = getOfferStateMeta(offer);
                                            const baseMeta = offer.validity || offer.description || offer.type || 'Campaign';
                                            const meta = offer.impressions > 0
                                                ? `${baseMeta} · shown to ${formatNumber(offer.impressions)} customers`
                                                : baseMeta;

                                            return (
                                                <tr
                                                    key={offer.id || index}
                                                    className="offers-table-row offers-table-row--clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Preview ${offer.title || 'offer'}`}
                                                    onClick={() => setPreviewOffer(offer)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            setPreviewOffer(offer);
                                                        }
                                                    }}
                                                >
                                                    <td data-label="Offer">
                                                        <div className="offers-table-offer">
                                                            <div
                                                                className="offers-table-icon"
                                                                style={{ background: OFFER_ICON_GRADIENTS[index % OFFER_ICON_GRADIENTS.length] }}
                                                            >
                                                                {getOfferIconText(offer)}
                                                            </div>
                                                            <div className="offers-table-summary">
                                                                <div className="offers-table-title">
                                                                    {offer.title || 'Untitled'}
                                                                    {offer.aiGenerated ? (
                                                                        <span className="offer-type-pill ai offers-table-ai-pill">AI</span>
                                                                    ) : null}
                                                                </div>
                                                                <div className="offers-table-meta">{meta}</div>
                                                                <div className="offers-table-discount">{offer.discount || '—'}</div>
                                                                {offer.createdAt ? (
                                                                    <div className="offers-table-created">Created {formatShortDateLabel(offer.createdAt)}</div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Status" data-align="center">
                                                        <div className="offers-table-status-cell">
                                                            <span className={`offers-table-status-pill ${state.key}`}>
                                                                {state.statusText}
                                                            </span>
                                                            {state.detailText ? (
                                                                <span className={`offers-table-state-badge ${state.key}`}>
                                                                    {state.detailText}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                    <td data-label="Redemptions" data-align="center">
                                                        <span
                                                            className="offers-table-stat"
                                                            title={offer.redemptionLimitType === 'per_user' && offer.redemptionLimitValue
                                                                ? `Up to ${formatNumber(offer.redemptionLimitValue)} per customer · no total cap`
                                                                : undefined}
                                                        >
                                                            {formatNumber(offer.redeemed)} / {offer.redemptionLimitType === 'global' && offer.redemptionLimitValue
                                                                ? formatNumber(offer.redemptionLimitValue)
                                                                : '—'}
                                                        </span>
                                                    </td>
                                                    <td data-label="Revenue" data-align="center">
                                                        <span className="offers-table-stat offers-table-stat--revenue">
                                                            <CurrencyValue value={offer.influencedRevenue} />
                                                        </span>
                                                    </td>
                                                    <td data-label="Actions" data-align="center">
                                                        <div className="offers-row-more" onClick={(event) => event.stopPropagation()}>
                                                            <button
                                                                className="offers-row-action-trigger touch-target"
                                                                type="button"
                                                                aria-label={`Open actions for ${offer.title || 'offer'}`}
                                                                aria-expanded={openActionOfferId === offer.id}
                                                                onClick={() => setOpenActionOfferId((current) => (current === offer.id ? null : offer.id))}
                                                            >
                                                                ⋯
                                                            </button>
                                                            <div className={`offers-row-action-menu ${openActionOfferId === offer.id ? 'open' : ''}`}>
                                                                <button type="button" onClick={() => { setOpenActionOfferId(null); handleEdit(offer); }}>Edit</button>
                                                                {offer.lifecycle === 'live' ? (
                                                                    <button type="button" onClick={() => { setOpenActionOfferId(null); handleToggleLifecycle(offer); }}>Pause</button>
                                                                ) : null}
                                                                {(offer.lifecycle === 'paused' || offer.lifecycle === 'scheduled') ? (
                                                                    <button type="button" onClick={() => { setOpenActionOfferId(null); handleSchedule(offer); }}>
                                                                        {offer.lifecycle === 'scheduled' ? 'Reschedule' : 'Schedule'}
                                                                    </button>
                                                                ) : null}
                                                                <button type="button" onClick={() => { setOpenActionOfferId(null); handleRemove(offer); }}>Delete</button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </section>
                )}

                </>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default StoreOffers;
